// Camera-domain cameras client.
//
// Backed by the BFF endpoints:
//   - GET /api/cameras                 (paginated list; `location_id` filter optional)
//   - GET /api/cameras/{doorbot_id}    (single camera)
//
// Server enforces grants — pages do NOT need to client-filter by location.
// All fields except `doorbot_id`, `location_id`, and `status` are best-effort
// (AVA may return null for older/inactive devices). Pages must be tolerant
// of nulls when rendering names/addresses/timestamps.

import { api } from "./client";
import { unwrapEnvelope } from "@/data/unwrapEnvelope";

export type CameraStatus = "online" | "offline" | "degraded" | string;

export interface Camera {
  doorbot_id: string;
  location_id: string;
  description?: string | null;
  kind?: string | null;
  status: CameraStatus;
  last_seen_at?: string | null;
  last_motion_at?: string | null;
  poster_refreshed_at?: string | null;
  capabilities?: Record<string, unknown> | null;
}

interface ListResponse {
  items: Camera[];
  next_cursor: string | null;
}

export interface ListCamerasOptions {
  /** Filter by location id; omit to return every camera in scope. */
  locationId?: string;
  /** Pagination cursor returned from a prior call. */
  cursor?: string;
  /** Page size hint. */
  limit?: number;
  signal?: AbortSignal;
}

export const camerasClient = {
  /** GET /api/cameras (single page). */
  async list(opts: ListCamerasOptions = {}): Promise<Camera[]> {
    const params = new URLSearchParams();
    if (opts.locationId) params.set("location_id", opts.locationId);
    if (opts.cursor) params.set("cursor", opts.cursor);
    if (opts.limit !== undefined) params.set("limit", String(opts.limit));
    const qs = params.toString();
    const path = qs.length === 0 ? "/api/cameras" : `/api/cameras?${qs}`;
    const res = await api.get<ListResponse>(path, { signal: opts.signal });
    return res.items ?? [];
  },

  /**
   * GET /api/cameras (full pagination). Walks `next_cursor` until exhausted.
   * Used by Recordings to seed the events:search doorbot list.
   */
  async listAll(opts: ListCamerasOptions = {}): Promise<Camera[]> {
    const out: Camera[] = [];
    let cursor: string | undefined = opts.cursor;
    // Bound the loop defensively — runaway cursors should never spin forever.
    for (let i = 0; i < 32; i++) {
      const params = new URLSearchParams();
      if (opts.locationId) params.set("location_id", opts.locationId);
      if (cursor) params.set("cursor", cursor);
      if (opts.limit !== undefined) params.set("limit", String(opts.limit));
      const qs = params.toString();
      const path = qs.length === 0 ? "/api/cameras" : `/api/cameras?${qs}`;
      const res = await api.get<ListResponse>(path, { signal: opts.signal });
      out.push(...(res.items ?? []));
      if (!res.next_cursor) break;
      cursor = res.next_cursor;
    }
    return out;
  },

  /** GET /api/cameras/{doorbotId}. Enveloped as {"camera": {...}}. */
  async get(doorbotId: string, signal?: AbortSignal): Promise<Camera> {
    const resp = await api.get<Camera>(
      `/api/cameras/${encodeURIComponent(doorbotId)}`,
      { signal },
    );
    return unwrapEnvelope(resp, "camera");
  },
};
